import { useEffect, useState } from 'react';
import {
  Box, Button, IconButton, TextField, Typography, Stack, Snackbar, Alert,
  Tooltip, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TelegramIcon from '@mui/icons-material/Telegram';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import StarsIcon from '@mui/icons-material/Stars';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import {
  doc, onSnapshot, setDoc, collection
} from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { db, auth } from '../firebase.js';

const T = {
  teal:        '#26b8b8',
  tealDark:    '#1ea0a0',
  tealLight:   '#e8f7f7',
  orange:      '#f57c00',
  orangeLight: '#fff3e0',
  purple:      '#7c6cf0',
  purpleLight: '#f0eeff',
  red:         '#e53935',
  redLight:    '#ffebee',
  gradient:    'linear-gradient(135deg, #26b8b8, #1ea0a0)',
  card:        '#ffffff',
  cardAlt:     '#f7f9fb',
  border:      '#dde3ec',
  text:        '#1a2233',
  sub:         '#4b5a6e',
  muted:       '#8a97a8',
};

const cardSx = {
  background: T.card,
  borderRadius: '16px',
  border: `1px solid ${T.border}`,
  boxShadow: '0 2px 12px rgba(30,60,90,0.07)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    background: T.cardAlt,
    fontSize: 13,
    '& fieldset': { borderColor: T.border },
    '&:hover fieldset': { borderColor: T.teal },
    '&.Mui-focused fieldset': { borderColor: T.teal },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: T.teal },
};

// ── Point categories shown on this page (userDoc field ↔ label/icon/color) ──

const POINT_FIELDS = [
  { key: 'ecoPoints',        labelEn: 'Ecological Points',       labelAm: 'Էկոլոգիական միավորներ',              icon: LocalFloristIcon,       color: T.teal },
  { key: 'volPoints',        labelEn: 'Volunteering Points',     labelAm: 'Կամավորական միավորներ',              icon: PeopleAltIcon,          color: T.purple },
  { key: 'homeworkPoints',   labelEn: 'Homework Logging Points', labelAm: 'Տնային աշխատանքի գրանցման միավորներ', icon: AssignmentTurnedInIcon, color: T.orange },
  { key: 'additionalPoints', labelEn: 'Additional Points',       labelAm: 'Լրացուցիչ միավորներ',                icon: StarsIcon,              color: T.red },
  { key: 'uploadCount',      labelEn: 'Notes Uploaded',          labelAm: 'Բեռնված նշումներ',                    icon: ArticleOutlinedIcon,    color: T.tealDark },
  { key: 'tutoringPoints',   labelEn: 'Tutoring Points',         labelAm: 'Դասավանդման միավորներ',              icon: SchoolOutlinedIcon,     color: T.purple },
];

// ── Loan helpers (same rule as Library.js) ───────────────────────────────────

function isOverdue(loan) {
  if (loan.returned || !loan.expiryDate) return false;
  const today = new Date().toISOString().slice(0, 10);
  return loan.expiryDate < today;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeader({ icon, title, accent }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: '10px',
        background: `${accent}14`, border: `1px solid ${accent}33`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 17, fontWeight: 700, color: T.text, letterSpacing: '-0.2px' }}>
        {title}
      </Typography>
    </Box>
  );
}

function PointCard({ field, value, language }) {
  const Icon = field.icon;
  return (
    <Box sx={{
      ...cardSx,
      p: 2, minWidth: 150, flex: '1 1 150px',
      display: 'flex', flexDirection: 'column', gap: 1,
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: '9px',
        background: `${field.color}14`, border: `1px solid ${field.color}33`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ color: field.color, fontSize: 17 }} />
      </Box>
      <Typography sx={{ fontSize: 11.5, color: T.muted, fontWeight: 600, lineHeight: 1.3 }}>
        {language ? field.labelEn : field.labelAm}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color: T.text, letterSpacing: '-0.3px' }}>
        {value || 0}
      </Typography>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function StudentProfile({ language }) {
  const [authUser, setAuthUser] = useState(null);
  const [userDocData, setUserDocData] = useState(null);
  const [loans, setLoans] = useState([]);

  const [editingUsername, setEditingUsername] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [savingUsername, setSavingUsername] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleSignOut = async () => {
    try { await signOut(auth); window.location.reload(); }
    catch (e) { console.error(e); }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => setAuthUser(user));
    return () => unsub();
  }, []);

  const emailLower = authUser?.email ? authUser.email.toLowerCase() : null;

  // Live userDoc listener — powers both the points cards and the username field.
  useEffect(() => {
    if (!emailLower) { setUserDocData(null); return; }
    const userDocRef = doc(db, 'peerTutoring', 'users', 'userDocs', emailLower);
    const unsub = onSnapshot(userDocRef, (snap) => {
      setUserDocData(snap.exists() ? snap.data() : {});
    });
    return () => unsub();
  }, [emailLower]);

  // Keep the editable field in sync with live data, unless the student is mid-edit.
  useEffect(() => {
    if (!editingUsername) setUsernameInput(userDocData?.username || '');
  }, [userDocData, editingUsername]);

  // Live library loans belonging to this student.
  useEffect(() => {
    if (!emailLower) { setLoans([]); return; }
    const unsub = onSnapshot(collection(db, 'library'), (snapshot) => {
      const rows = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((row) => String(row.studentEmail || '').trim().toLowerCase() === emailLower)
        .sort((a, b) => {
          const da = a.createdAt?.toMillis?.() ?? 0;
          const db_ = b.createdAt?.toMillis?.() ?? 0;
          return db_ - da;
        });
      setLoans(rows);
    });
    return () => unsub();
  }, [emailLower]);

  const handleSaveUsername = async () => {
    if (!emailLower) return;
    setSavingUsername(true);
    try {
      await setDoc(
        doc(db, 'peerTutoring', 'users', 'userDocs', emailLower),
        { username: usernameInput.trim() },
        { merge: true }
      );
      showSnack(language ? 'Telegram username saved.' : 'Telegram օգտանունը պահպանվեց։', 'success');
      setEditingUsername(false);
    } catch (e) {
      console.error(e);
      showSnack(language ? 'Could not save username.' : 'Չհաջողվեց պահպանել օգտանունը։', 'error');
    }
    setSavingUsername(false);
  };

  const handleCancelEditUsername = () => {
    setUsernameInput(userDocData?.username || '');
    setEditingUsername(false);
  };

  const overdueCount = loans.filter(isOverdue).length;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ pb: 6 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: T.text, letterSpacing: '-0.4px' }}>
            {language ? 'My Profile' : 'Իմ Էջը'}
          </Typography>
          {overdueCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
              <WarningAmberIcon sx={{ color: T.red, fontSize: 15 }} />
              <Typography sx={{ fontSize: 12.5, color: T.red, fontWeight: 600 }}>
                {language
                  ? `${overdueCount} overdue book${overdueCount > 1 ? 's' : ''}`
                  : `${overdueCount} ժամկետանց գիրք`}
              </Typography>
            </Box>
          )}
        </Box>
        <Tooltip title={language ? 'Sign Out' : 'Ելք'} arrow>
          <Button
            onClick={handleSignOut}
            startIcon={<LogoutIcon />}
            sx={{
              background: T.card, border: `1px solid ${T.border}`,
              color: T.sub, borderRadius: '10px',
              fontWeight: 500, fontSize: 13, textTransform: 'none', px: 2.5,
              boxShadow: '0 1px 4px rgba(30,60,90,0.06)',
              '&:hover': { borderColor: T.teal, color: T.teal, background: T.tealLight },
            }}
          >
            {language ? 'Sign Out' : 'Ելք'}
          </Button>
        </Tooltip>
      </Box>

      {/* ── Points overview ── */}
      <SectionHeader
        icon={<EmojiEventsIcon sx={{ color: T.teal, fontSize: 19 }} />}
        title={language ? 'My Points' : 'Իմ միավորները'}
        accent={T.teal}
      />

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 4 }}>
        {POINT_FIELDS.map((field) => (
          <PointCard
            key={field.key}
            field={field}
            value={userDocData?.[field.key]}
            language={language}
          />
        ))}
      </Stack>

      {/* ── Telegram username ── */}
      <SectionHeader
        icon={<TelegramIcon sx={{ color: T.purple, fontSize: 19 }} />}
        title={language ? 'Telegram Username' : 'Telegram օգտանուն'}
        accent={T.purple}
      />

      <Box sx={{ ...cardSx, p: 3, mb: 4 }}>
        {editingUsername ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
            <TextField
              fullWidth
              size="small"
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
              placeholder={language ? '@yourusername' : '@ձեր_օգտանունը'}
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleSaveUsername()}
              sx={fieldSx}
            />
            <Stack direction="row" spacing={1}>
              <Button
                onClick={handleSaveUsername}
                disabled={savingUsername}
                startIcon={<CheckIcon />}
                sx={{
                  background: T.gradient, color: '#fff',
                  borderRadius: '10px', fontWeight: 600, fontSize: 13,
                  textTransform: 'none', px: 2,
                  boxShadow: '0 3px 12px rgba(38,184,184,0.30)',
                  '&:hover': { background: 'linear-gradient(135deg, #1ea0a0, #178e8e)' },
                }}
              >
                {language ? 'Save' : 'Պահպանել'}
              </Button>
              <Button
                onClick={handleCancelEditUsername}
                disabled={savingUsername}
                startIcon={<CloseIcon />}
                sx={{
                  background: T.card, border: `1px solid ${T.border}`,
                  color: T.sub, borderRadius: '10px',
                  fontWeight: 500, fontSize: 13, textTransform: 'none', px: 2,
                  '&:hover': { borderColor: T.red, color: T.red, background: T.redLight },
                }}
              >
                {language ? 'Cancel' : 'Չեղարկել'}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Typography sx={{ fontSize: 15, color: userDocData?.username ? T.text : T.muted, fontWeight: 600 }}>
              {userDocData?.username || (language ? 'No username set yet.' : 'Օգտանունը դեռ նշված չէ։')}
            </Typography>
            <Tooltip title={language ? 'Edit' : 'Խմբագրել'} arrow>
              <IconButton
                size="small"
                onClick={() => setEditingUsername(true)}
                sx={{
                  color: T.purple,
                  '&:hover': { background: T.purpleLight },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>

      {/* ── Borrowed books ── */}
      <SectionHeader
        icon={<MenuBookIcon sx={{ color: T.orange, fontSize: 19 }} />}
        title={language ? 'My Borrowed Books' : 'Իմ վարկած գրքերը'}
        accent={T.orange}
      />

      <Box sx={{ ...cardSx, overflow: 'hidden', mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: T.purpleLight }}>
                {[
                  language ? 'Book'     : 'Գիրք',
                  language ? 'Serial'   : 'Համար',
                  language ? 'Borrowed' : 'Վարկի ամսաթիվ',
                  language ? 'Due date' : 'Վերադարձի ամսաթիվ',
                  language ? 'Status'   : 'Կարգավիճակ',
                ].map((label, i) => (
                  <TableCell
                    key={i}
                    sx={{
                      fontWeight: 700, color: T.purple, fontSize: 12.5,
                      letterSpacing: '0.4px', textTransform: 'uppercase',
                      borderBottom: `2px solid ${T.purple}22`,
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loans.map((loan) => {
                const overdue = isOverdue(loan);
                return (
                  <TableRow
                    key={loan.id}
                    sx={{
                      background: overdue ? `${T.red}07` : 'transparent',
                      '&:hover': { background: overdue ? `${T.red}12` : T.cardAlt },
                      transition: 'background 0.15s',
                    }}
                  >
                    <TableCell sx={{ borderBottom: `1px solid ${T.border}` }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {overdue && (
                          <Tooltip title={language ? 'Overdue — not yet returned' : 'Ժամկետանց — դեռ չի վերադարձվել'} arrow>
                            <WarningAmberIcon sx={{ color: T.red, fontSize: 16, flexShrink: 0 }} />
                          </Tooltip>
                        )}
                        <Typography sx={{ color: overdue ? T.red : T.text, fontSize: 13, fontWeight: overdue ? 600 : 400 }}>
                          {loan.bookName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: T.sub, fontSize: 13, borderBottom: `1px solid ${T.border}` }}>
                      {loan.serialNumber}
                    </TableCell>
                    <TableCell sx={{ color: T.muted, fontSize: 13, borderBottom: `1px solid ${T.border}` }}>
                      {loan.loanDate}
                    </TableCell>
                    <TableCell sx={{ borderBottom: `1px solid ${T.border}` }}>
                      {loan.expiryDate ? (
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.5,
                          px: 1.25, py: 0.4, borderRadius: '7px',
                          background: overdue ? T.redLight : T.orangeLight,
                          border: `1px solid ${overdue ? T.red : T.orange}33`,
                        }}>
                          {overdue && <WarningAmberIcon sx={{ fontSize: 12, color: T.red }} />}
                          <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: overdue ? T.red : T.orange }}>
                            {loan.expiryDate}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ color: T.muted, fontSize: 13 }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ borderBottom: `1px solid ${T.border}` }}>
                      <Chip
                        size="small"
                        label={loan.returned
                          ? (language ? 'Returned' : 'Վերադարձված')
                          : (language ? 'On loan' : 'Վարկված')}
                        sx={{
                          height: 22, fontSize: 11, fontWeight: 600,
                          background: loan.returned ? T.tealLight : T.orangeLight,
                          color: loan.returned ? T.teal : T.orange,
                          border: `1px solid ${loan.returned ? T.teal : T.orange}33`,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}

              {loans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: T.muted, fontSize: 13 }}>
                    {language ? 'No borrowed books yet.' : 'Վարկած գրքեր դեռ չկան։'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{
            borderRadius: '10px',
            border: `1px solid ${snackbar.severity === 'error' ? '#fca5a5' : '#6ee7b7'}`,
            boxShadow: '0 4px 16px rgba(30,60,90,0.10)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}